import { useCallback, useEffect, useRef } from "react";
import { ButtonBase, Icon, Box, styled } from "@mui/material";
import { ChevronRight } from "@mui/icons-material";
import { useLocation } from "react-router-dom";
import clsx from "clsx";

const NavExpandRoot = styled("div")(({ theme }) => ({
  "& .expandIcon": {
    transform: "rotate(90deg)",
    transition: "transform 0.3s",
  },
  "& .collapseIcon": {
    transform: "rotate(0deg)",
    transition: "transform 0.3s",
  },
  "& .expansion-panel": {
    overflow: "hidden",
    transition: "max-height 0.3s",
  },
}));

export const BaseButton = styled(ButtonBase)(({ theme }) => ({
  height: 44,
  width: "100%",
  overflow: "hidden",
  paddingRight: "16px",
  borderRadius: "4px",
  marginBottom: "8px",
  display: "flex",
  justifyContent: "space-between !important",
  color: "white",
  "&:hover": { background: "rgba(255, 255, 255, 0.08)" },
  "& .icon": {
    width: 36,
    fontSize: "18px",
    paddingLeft: "16px",
    paddingRight: "16px",
  },
}));

export const BulletIcon = styled("div")(({ theme }) => ({
  width: 4,
  height: 4,
  color: "inherit",
  overflow: "hidden",
  marginLeft: "20px",
  marginRight: "8px",
  borderRadius: "50%",
  background: theme.palette.text.primary,
}));

export const ItemText = styled("span")(() => ({
  fontSize: "0.875rem",
  paddingLeft: "0.8rem",
  verticalAlign: "middle",
}));

export const BadgeValue = styled("div")(() => ({
  padding: "1px 4px",
  overflow: "hidden",
  borderRadius: "300px",
}));

export default function MatxVerticalNavExpansionPanel({
  item,
  children,
  mode,
  expandedParent,
  setExpandedParent,
}) {
  const elementRef = useRef(null);
  const componentHeight = useRef(0);
  const { pathname } = useLocation();
  const { name, icon, iconText, badge } = item;

  const isExpanded = expandedParent === name;

  const handleClick = () => {
    componentHeight.current = 0;
    calculateHeight(elementRef.current);
    setExpandedParent(isExpanded ? null : name);
  };

  const calculateHeight = useCallback((node) => {
    if (!node) return;
    for (let child of node.children || []) {
      calculateHeight(child);
    }
    if (node.name === "child") componentHeight.current += node.scrollHeight;
    else componentHeight.current += 44;
  }, []);

  useEffect(() => {
    if (!elementRef.current) return;
    calculateHeight(elementRef.current);
    for (let child of elementRef.current.children) {
      if (child.getAttribute("href") === pathname) {
        setExpandedParent(name);
      }
    }
  }, [pathname, calculateHeight, name, setExpandedParent]);

  return (
    <NavExpandRoot>
      <BaseButton
        className={clsx({
          compactNavItem: mode === "compact",
          open: isExpanded,
        })}
        onClick={handleClick}
      >
        <Box display="flex" alignItems="center">
          {icon && <Icon className="icon">{icon}</Icon>}
          {iconText && <BulletIcon />}
          <ItemText className="sidenavHoverShow">{name}</ItemText>
        </Box>

        {badge && (
          <BadgeValue className="sidenavHoverShow itemIcon">
            {badge.value}
          </BadgeValue>
        )}

        <div
          className={clsx({
            collapseIcon: !isExpanded,
            expandIcon: isExpanded,
          })}
        >
          <ChevronRight fontSize="small" sx={{ verticalAlign: "middle" }} />
        </div>
      </BaseButton>

      {/* <div
        ref={elementRef}
        className="expansion-panel submenu"
        style={
          isExpanded
            ? { maxHeight: componentHeight.current + "px" }
            : { maxHeight: "0px" }
        }
      >
        {children}
      </div> */}
      <div
        ref={elementRef}
        className="expansion-panel submenu"
        style={
          isExpanded
            ? { maxHeight: componentHeight.current + "px" }
            : { maxHeight: "0px" }
        }
      >
        <Box
          sx={{
            maxHeight: "calc(100vh - 150px)", // adjust based on header/footer
            overflowY: "auto",
            overflowX: "hidden",
            px: 1, // horizontal padding for items
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#555",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: "#777",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "transparent",
            },
          }}
        >
          {children}
        </Box>
      </div>
    </NavExpandRoot>
  );
}
